import { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { auth, db } from "../../../firebase/config";
import "./Enrolltoaschool.css";

const Enrolltoaschool = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [schools, setSchools] = useState<{ id: string; schoolName: string }[]>([]);
  const [schoolId, setSchoolId] = useState("");
  const [message, setMessage] = useState("");
  const [uid, setUid] = useState<string | null>(null);
  const [signedInEmail, setSignedInEmail] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUid(user.uid);
      setSignedInEmail(user.email);
    } else {
      console.error("❌ No user is authenticated.");
    }
  }, []);

  const togglePopup = () => {
    setShowPopup(!showPopup);
    setMessage("");
    setShowSuccess(false);
  };

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const schoolsCollection = collection(db, "schools");
        const schoolSnapshot = await getDocs(schoolsCollection);

        const schoolList: { id: string; schoolName: string }[] = [];

        for (const docSnap of schoolSnapshot.docs) {
          const schoolId = docSnap.id;
          const detailsQuery = await getDocs(collection(db, "schools", schoolId, "schoolDetails"));
          if (!detailsQuery.empty) {
            const data = detailsQuery.docs[0].data();
            schoolList.push({
              id: schoolId,
              schoolName: data.name || "Unnamed School",
            });
          }
        }

        setSchools(schoolList);
      } catch (error) {
        console.error("❌ Error fetching schools:", error);
      }
    };

    fetchSchools();
  }, []);

  const handleEnroll = async () => {
    if (!uid || !signedInEmail) {
      setMessage("User not authenticated. Please log in to enroll.");
      return;
    }

    if (!schoolId) {
      setMessage("Please select a school.");
      return;
    }

    try {
      // ✅ Check if already enrolled
      const alreadyEnrolledDoc = doc(db, "users", uid, "schoolspartof", schoolId);
      const alreadyEnrolledSnap = await getDoc(alreadyEnrolledDoc);
      if (alreadyEnrolledSnap.exists()) {
        alert("⚠️ You are already enrolled in this school.");
        return;
      }

      // ✅ Check if user is authorized by email
      const authorizedRef = collection(db, `schools/${schoolId}/schoolDetails/main/authorizedpersons`);
      const authorizedSnap = await getDocs(authorizedRef);

      const isAuthorized = authorizedSnap.docs.some(
        (docSnap) => docSnap.data().email === signedInEmail
      );

      if (!isAuthorized) {
        setMessage("❌ You have not been enrolled in this school. Kindly contact the school admin.");
        return;
      }

      const selectedSchool = schools.find((school) => school.id === schoolId);
      const schoolName = selectedSchool?.schoolName || "Unnamed School";

      // ✅ Save teacher school membership
      await setDoc(doc(db, "users", uid, "schoolspartof", schoolId), {
        role: "teacher",
        schoolName: schoolName,
        schoolUID: schoolId,
      });

      // ✅ Save teacher UID in authorizedpersons so rules can grant access
      await setDoc(doc(db, `schools/${schoolId}/schoolDetails/main/authorizedpersons`, uid), {
        email: signedInEmail,
        linkedBy: uid,
        timestamp: new Date().toISOString(),
      });

      setMessage("");
      setShowSuccess(true);
    } catch (error) {
      console.error("❌ Enrollment error:", error);
      setMessage("An error occurred during enrollment.");
    }
  };

  return (
    <div className="enrolltoaschool-layout">
      <div className={`enrolltoaschool-content ${showPopup ? "enrolltoaschool-blur-background" : ""}`}>
        <button onClick={togglePopup} className="enrolltoaschool-open-button">
          Enroll to a School
        </button>
      </div>

      {showPopup && (
        <div className="enrolltoaschool-overlay">
          <div className="enrolltoaschool-box">
            <h2 className="enrolltoaschool-box-title">Enroll to a School</h2>
            <p className="enrolltoaschool-box-text">Fill in the details below to proceed with enrollment.</p>

            <input
              className="enrolltoaschool-box-input"
              type="email"
              placeholder="Email"
              value={signedInEmail || ""}
              readOnly
            />

            <label htmlFor="school-select" className="enrolltoaschool-box-label">Select a school:</label>
            <select
              id="school-select"
              className="enrolltoaschool-box-select"
              value={schoolId}
              onChange={(e) => setSchoolId(e.target.value)}
            >
              <option value="">Select school</option>
              {schools.map((school) => (
                <option key={school.id} value={school.id}>{school.schoolName}</option>
              ))}
            </select>

            {message && (
              <div className="enrolltoaschool-message">
                <p>{message}</p>
              </div>
            )}

            <div className="enrolltoaschool-box-buttons">
              <button className="enrolltoaschool-button-cancel" onClick={togglePopup}>Cancel</button>
              <button className="enrolltoaschool-button-confirm" onClick={handleEnroll}>Enroll</button>
            </div>
          </div>
        </div>
      )}

      {showSuccess && (
        <div className="enrolltoaschool-success-popup">
          <div className="enrolltoaschool-success-content">
            <h3>🎉 Enrolled Successfully!</h3>
            <p>You now have access to the selected school’s resources.</p>
            <button onClick={() => setShowSuccess(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Enrolltoaschool;
